import {useCallback, useContext, useEffect, useMemo} from 'react'
import _ from 'lodash'
// import { useNavigate } from "react-router";
// import { Icon } from "semantic-ui-react";
import {BiTrash} from 'react-icons/bi'
import {FaExpandArrowsAlt} from 'react-icons/fa'
import {JsonViewer} from '@/components/json-viewer'
import {DataSourcesContext, IDataSourceItem} from '@/features/data-sources'
// import { ExpressionConfigMap, IParamData } from "@/features/expressions";
// import { IExpressionState } from "@/features/expressions/routes/expressions";
import {
  IMixerChannel,
  IMixerChannelCollectionProps,
  MixerChannelCollection,
} from '../channel-collection'
import {ChannelTemplateSelector, IDropdownItem} from '../channel-templates'
import {IFieldDescriptor, IMixerConfig, useChannelComponents} from '../..'
import {defaultCutStepSize, defaultLimitChannels, itemLimit} from './const'
import './style.css'

export {defaultLimitChannels, itemLimit}
export type SemanticFilteringItemType = IDataSourceItem & {sortScore?: number; position?: number}
export type SemanticFilteringChannelKeys = 'adjust' | 'limit'

export interface ISemanticFilteringComponentProps {
  sortScoreField?: string
  config: Partial<IMixerConfig>
  channels?: Record<SemanticFilteringChannelKeys, IMixerChannel[]>
  defaultChannelValue?: IMixerChannel['value']
  extraFields?: IFieldDescriptor[]
  sortEpsilon?: number
  channelProps?: IMixerChannelCollectionProps['channelProps']
  actions?: React.ReactNode
  itemFilter?: (item: SemanticFilteringItemType) => boolean
  onChannelsChanged: (channels: Record<SemanticFilteringChannelKeys, IMixerChannel[]>) => void
  onResultChanged?: (items: SemanticFilteringItemType[]) => void
}

export function SemanticFilteringComponent(props: ISemanticFilteringComponentProps) {
  const {
    sortScoreField,
    config,
    channels,
    defaultChannelValue,
    extraFields,
    // sortEpsilon,
    channelProps,
    actions,
    itemFilter,
    onChannelsChanged,
    onResultChanged,
  } = {
    sortScoreField: 'sortScore',
    defaultChannelValue: {
      isInitialized: true,
      sort: 1,
      cut: {min: defaultCutStepSize, max: 1},
      range: {min: 0, max: 1},
    },
    // sortEpsilon: 0.001,
    ...props,
  }

  const {mergedItems} = useContext(DataSourcesContext)

  const extraOptions = useMemo<IDropdownItem[]>(() => {
    return _.map(extraFields, (o) => {
      const opt: IDropdownItem = {
        ...o,
        value: {
          ...defaultChannelValue,
          ...o,
          isInitialized: o.initView ? false : true,
        },
      }
      return opt
    })
  }, [extraFields])

  const {sort, cut, calculateSortScores, calculateCuts} = useChannelComponents(channels?.adjust)

  useEffect(() => {
    // sort and filter all items
    let res = _.chain(mergedItems as SemanticFilteringItemType[])
    if (itemFilter) {
      res = res.filter(itemFilter)
    }
    if (sort && sort.length > 0) {
      res = res
        .map((v) => {
          const {mean} = calculateSortScores(v)
          // if (Math.abs(mean) < sortEpsilon) return;
          const _resItem: SemanticFilteringItemType = {
            ...v,
            [sortScoreField]: mean,
          }
          return _resItem
        })
        .compact()
      res = res.orderBy([sortScoreField], ['desc'])
    } else {
      res = res.sortBy(['title'], ['asc'])
    }
    if ((cut && cut.length > 0) || channels?.limit) {
      res = res.filter((v) => {
        const {shouldCut} = calculateCuts(v)
        return !shouldCut
      })
    }
    if (channels?.limit && channels?.limit.length > 0) {
      res = res
        .map(
          (v, i) =>
            ({
              ...v,
              position: i + 1,
            }) as SemanticFilteringItemType
        )
        .filter((v) => {
          let keep = true
          _.forEach(channels.limit, ({value, descriptor}) => {
            if (value.mute) return
            if (!value.cut) return
            const score = _.get(v, descriptor.sourcePath)
            if (_.isNumber(score)) {
              if (score < value.cut.min || score > value.cut.max) {
                keep = false
              }
            }
            if (!keep) return false
          })
          return keep
        })
    }
    onResultChanged?.(res.value())
  }, [mergedItems, sort, cut, channels?.limit, config, itemFilter])

  const handleResetChannels = useCallback(() => {
    if (onChannelsChanged)
      onChannelsChanged({
        adjust: [],
        limit: defaultLimitChannels,
      })
  }, [onChannelsChanged])

  const handleExpandChannelValues = useCallback(() => {
    if (!channels) return
    if (!onChannelsChanged) return
    const scaleBy =
      1 /
      _.chain(channels.adjust)
        .map((v) => Math.abs(v.value.sort))
        .reduce((a, b) => _.max([a, b]) || 0)
        .value()
    const scaledChannels = _.chain(channels.adjust)
      .map((v) => ({
        ...v,
        value: {
          ...v.value,
          sort: v.value.sort * scaleBy,
        },
      }))
      .value()
    onChannelsChanged({
      ...channels,
      adjust: scaledChannels,
    })
  }, [channels, onChannelsChanged])

  const handleOnAddChannel = useCallback(
    (channel: IMixerChannel) => {
      // TODO: something better
      const {onAdded, ...rest} = channel.descriptor
      if (onAdded) onAdded(rest)
      if (!onChannelsChanged) return
      const adjust: IMixerChannel[] = [...(channels?.adjust || [])]
      const sourceExists =
        adjust.map((c) => c.descriptor.sourcePath).indexOf(channel.descriptor.sourcePath) !== -1
      if (!channel.value.isInitialized || !sourceExists) {
        adjust.push(channel)
      }
      onChannelsChanged({
        limit: defaultLimitChannels,
        ...channels,
        adjust,
      })
    },
    [channels, onChannelsChanged]
  )

  return (
    <div className='semantic-filtering'>
      <div className='channel-group'>
        <div className='actions'>
          <div>
            {/* <button onClick={handleNavigateToExpression}>
                        <Icon name='terminal' />
                        send to /expressions
                    </button> */}
            <button
              className='red'
              onClick={handleResetChannels}
            >
              <BiTrash />
              reset
            </button>
          </div>
          {actions ?
            <div className='extra'>{actions}</div>
          : null}
        </div>
        <MixerChannelCollection
          simplified={!config.showAdvancedControls}
          channels={channels?.adjust}
          channelProps={channelProps}
          actions={
            <>
              <ChannelTemplateSelector
                extraOptions={extraOptions}
                onAdd={handleOnAddChannel}
              />
              {config.showAdvancedControls && channels?.adjust && channels.adjust.length > 0 ?
                <div style={{display: 'flex', justifyContent: 'end', fontSize: '0.8em'}}>
                  <button onClick={handleExpandChannelValues}>
                    <FaExpandArrowsAlt />
                    &nbsp; expand weights
                  </button>
                </div>
              : null}
            </>
          }
          onChannelsChanged={(value) => {
            if (!channels) return
            onChannelsChanged({
              ...channels,
              adjust: value,
            })
          }}
        />
        {!config.showAdvancedControls ? null : (
          <JsonViewer
            data={channels}
            style={{
              marginTop: '1em',
            }}
          />
        )}
      </div>
      <div className='channel-group'>
        <MixerChannelCollection
          channels={channels?.limit}
          canDelete={false}
          channelProps={{
            showSolo: false,
            canSort: false,
            disabled: !channels?.adjust || channels.adjust.length === 0,
          }}
          onChannelsChanged={(value) => {
            if (!channels) return
            onChannelsChanged({
              ...channels,
              limit: value,
            })
          }}
        />
      </div>
    </div>
  )
}
